import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

type CommissionStatus = 'PENDING' | 'ACCEPTED' | 'IN_PROGRESS' | 'DELIVERED' | 'COMPLETED' | 'CANCELLED' | 'DISPUTED';

interface Commission {
  id: string;
  creatorId: string;
  creatorName?: string;
  title: string;
  description: string;
  amount: number;
  currency: string;
  status: CommissionStatus;
  deadline?: string;
  createdAt: string;
  acceptedAt?: string;
  deliveredAt?: string;
  completedAt?: string;
  needsPaymentAuth?: boolean;
}

interface CommissionStats {
  total: number;
  pending: number;
  active: number;
  completed: number;
  totalSpent: number;
}

const PatronCommissionsPage: React.FC = () => {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [stats, setStats] = useState<CommissionStats>({
    total: 0,
    pending: 0,
    active: 0,
    completed: 0,
    totalSpent: 0,
  });
  const [filter, setFilter] = useState<CommissionStatus | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCommissions();
  }, [filter]);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams();
      params.append('type', 'requested'); // Patron requests commissions
      if (filter !== 'all') params.append('status', filter);
      
      const res = await axios.get(`${API_BASE_URL}/api/commissions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data;
      setCommissions(data.commissions || []);
      
      // Calculate stats
      const all = data.commissions || [];
      setStats({
        total: all.length,
        pending: all.filter((c: Commission) => c.status === 'PENDING').length,
        active: all.filter((c: Commission) => 
          ['ACCEPTED', 'IN_PROGRESS', 'DELIVERED'].includes(c.status)
        ).length,
        completed: all.filter((c: Commission) => c.status === 'COMPLETED').length,
        totalSpent: all
          .filter((c: Commission) => c.status === 'COMPLETED')
          .reduce((sum: number, c: Commission) => sum + c.amount, 0),
      });

      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: CommissionStatus) => {
    const colors: Record<CommissionStatus, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      ACCEPTED: 'bg-blue-100 text-blue-800',
      IN_PROGRESS: 'bg-purple-100 text-purple-800',
      DELIVERED: 'bg-indigo-100 text-indigo-800',
      COMPLETED: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
      DISPUTED: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status]}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Commissions</h1>
            <p className="text-gray-600 mt-1">Track your commission requests and deliverables</p>
          </div>
          <Link
            to="/dashboard/commissions/new"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            + New Commission
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Total Requests</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Active</p>
          <p className="text-2xl font-bold text-blue-600">{stats.active}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Completed</p>
          <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Total Spent</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(stats.totalSpent, 'USD')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex overflow-x-auto">
          {[
            { key: 'all', label: 'All' },
            { key: 'PENDING', label: 'Pending' },
            { key: 'ACCEPTED', label: 'Accepted' },
            { key: 'IN_PROGRESS', label: 'In Progress' },
            { key: 'DELIVERED', label: 'Delivered' },
            { key: 'COMPLETED', label: 'Completed' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key as any)}
              className={`px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                filter === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Commissions List */}
      {commissions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-600 mb-4">No commissions found</p>
          <Link
            to="/dashboard/commissions/new"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Request Your First Commission
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {commissions.map((commission) => (
            <div key={commission.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <Link to={`/dashboard/commissions/patron/${commission.id}`} className="block p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{commission.title}</h3>
                      {getStatusBadge(commission.status)}
                      {commission.needsPaymentAuth && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                          Payment Required
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Creator: {commission.creatorName || 'Unknown'}
                    </p>
                    <p className="text-sm text-gray-700 line-clamp-2">{commission.description}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(commission.amount, commission.currency)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Requested {formatDate(commission.createdAt)}
                    </p>
                  </div>
                </div>

                {/* Status Info */}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  {commission.deadline && (
                    <span>Deadline: {formatDate(commission.deadline)}</span>
                  )}
                  {commission.acceptedAt && (
                    <span>Accepted: {formatDate(commission.acceptedAt)}</span>
                  )}
                  {commission.deliveredAt && (
                    <span>Delivered: {formatDate(commission.deliveredAt)}</span>
                  )}
                  {commission.status === 'DELIVERED' && (
                    <span className="text-blue-600 font-medium">⚡ Ready to approve</span>
                  )}
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatronCommissionsPage;
